//AgriculturalUse.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as ExpandOnPhone from "../Widgets/ExpandOnPhone";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as SignIn_Auth0 from "../Util/SignIn_Auth0";
import * as SnackBar from "../Util/SnackBar";
import * as SoilTaxRate from "../SoilTaxRate";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Widgets from "../Widgets/Widgets";

import { Button, ButtonGroup, ButtonProps, Checkbox, CssBaseline, FormControlLabel, Icon, IconButton, Paper, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import { blueGrey, yellow } from '@mui/material/colors';

import Axios, { AxiosResponse } from "axios";

import React from "react";
import ReactDOM from "react-dom/client";

//ESRI javascript API.
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";

interface Graphic_extended extends __esri.Graphic {
   myId: string;
   agUseType: string;
}

const toolName: string = "Agricultural Use";

let agUseType: string = "Timber";   // Timber, Crop, or "".
let activeTool: "point" | "multipoint" | "polyline" | "polygon" | "rectangle" | "circle" | "mesh" = null;

let drawingMode: "hybrid" | "freehand" | "click" = "click";       // click or hybrid. hybrid is freehand.
let activeGra: Graphic_extended = null;   // Active graphic being drawn or updated.

let curPnt: __esri.Point;   // Current mouse move point.

let updateTool: "move" | "transform" | "reshape";       // reshape, transform, or move.

let drawingState: "active" | "start" | "complete" | "cancel" = null; // start, active, complete or cancel or "".
let updateState: "active" | "start" | "complete" = null; // start, active, complete, cancel, or "".

const minArea = 0.01; // Minimum size in acres of an ag use polygon.
const minScreenAreaForLabel = 3000; //pixels.

let entireParcel: boolean = true;     //True means select entire parcel for processing.

let sketchVM: __esri.SketchViewModel;             // API sketch view model.
let taxRate6Percent: boolean = false; // True means 6 percent tax rate.

let areaLabelGra: __esri.Graphic; // Graphic for area text symbol.
let helpMsg: string;

let query: __esri.Query;
let parcelFeature: __esri.Graphic;
let parcelPoly: __esri.Polygon;
let deedAcres: number;
let deedAcresScaleFactor: number;
let printing: boolean = false;

let click_eventHandler: IHandle;

// Left panel results.
interface iResult {
   agUseType: string; // "Timber", "Crop"
   area: number;      // 1.23 acre
   soilType: string;  // MeC, etc.
   taxCode: string;   // 2T, 3C, etc.
   taxValue: number;  // $100 per acre
   gra: Graphic_extended;
}

let pin: string; //PIN of ag use parcel.
let parcelAcres: number; //Parcel acreage.
let deedAcresDeviation: number;
let areaText: string;
let allResults: iResult[];
let results: iResult[] = [];
let totalValue: number = 0;
let totalAcres: number = 0;
let totalLandValue: number; //From server to compute price per acre.

//Dropdown menu items.
let areaUnitLabel: string = "";

const totalSymbol = {
   type: "text",
   angle: 0,
   color: "black",
   font: {
      size: 11,
      weight: "bold",
   },
   haloColor: "white",
   haloSize: 2,
   text: "",
   verticalAlignment: "middle",
};

//Clear this tool.
export function clearTool() {
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      //Load the sketch view model module.
      import("@arcgis/core/widgets/Sketch/SketchViewModel").then(({ default: SketchViewModel }) => {

         //Create the sketch view model.
         sketchVM = new SketchViewModel({
            layer: GraLay.agUse,
            view: MainMap.activeView,
            polylineSymbol: Settings.agUseLineSym,
            polygonSymbol: Settings.agUsePolygonSym,
            updateOnGraphicClick: true,
            defaultCreateOptions: { mode: drawingMode },
            defaultUpdateOptions: { tool: "reshape", toggleToolOnClick: false, enableRotation: false, enableScaling: true, multipleSelectionEnabled: false },
         });

         //Create events for the sketch view model.
         sketchVM.on("create", function (event) {
            //Util.errMsg(" Event.State: " + event.state, event);
            drawingState = event.state;

            let vertexAddInfo: __esri.VertexAddEventInfo = null;
            let cursorUpdateInfo: __esri.CursorUpdateEventInfo = null;
            let toolType: string = "";

            if (event.toolEventInfo) {
               const toolEventInfo = event.toolEventInfo;

               if (toolEventInfo.type === "vertex-add") {
                  vertexAddInfo = toolEventInfo;
                  toolType = vertexAddInfo.type;

               } else {
                  cursorUpdateInfo = toolEventInfo;
                  toolType = cursorUpdateInfo.type;
               }
            }

            // First point of polygon or point. --------------------------------------------------------------
            if (drawingState === "start") {
               activeTool = event.tool;
               activeGra = null;
               updateTool = null;
               areaLabelGra = null;

            // Draw in progress. (mouse move) ------------------------------------------
            } else if (drawingState === "active") {
               activeGra = event.graphic as Graphic_extended;

               //Set the id and drawing tool name on the active graphic.
               if (activeGra) {
                  if (!activeGra.attributes) {
                     activeGra.attributes = { myId: Util.uid(), agUseType: agUseType };
                  }
               }

               //Save mouse move point.
               if (toolType === "cursor-update") {
                  const x = cursorUpdateInfo.coordinates[0];
                  const y = cursorUpdateInfo.coordinates[1];
                  curPnt = new Point({ x: x, y: y, spatialReference: MainMap.activeView.spatialReference });
                  buildLabels();
               }

               //Double click. ----------------------
            } else if (drawingState === "complete") {
               if (event.tool === "point") {
                  activeGra = event.graphic as Graphic_extended;
                  activeGra.attributes = { myId: Util.uid(), agUseType: agUseType };
                  areaLabelGra = null;

                  //Build query to get the parcel polygon geometry.
                  query = new Query({
                     returnGeometry: true,
                     outSpatialReference: MainMap.activeView.spatialReference,
                     geometry: activeGra.geometry,
                     maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
                     geometryPrecision: 0,
                     outFields: [Settings.pin_fieldName],
                  });

                  const parcelLaySpec: Settings.iLayer = Util_App.getLayerSpec(Settings.parcelLayerName);
                  const url: string = Util_App.getLayerURL(parcelLaySpec);
                  QueryTask.executeQueryJSON(url, query).then(getParcelGeometry_success).catch(queryTask_failed);

               } else {
                  computeAgUse();
                  buildLabels(true);
                  disableDrawingTool();
               }

               // Draw was canceled. --------------------------------------------------
            } else if (drawingState === "cancel") {
               activeGra = null;
               activeTool = null;
            }

            setHelpMessage();
            refresh();
         });

         //Update graphic edit event.
         sketchVM.on("update", function (event) {

            if (DrawerMenu.curToolName !== "Agricultural Use") {
               setTimeout(function () { sketchVM.cancel(); }, 100);
               return;
            }

            updateState = event.state;
            updateTool = event.tool;
            drawingState = null;

            if (updateState === "start") { //First time. ---------------------------------------
               activeGra = event.graphics[0] as Graphic_extended;
               areaLabelGra = null;
               computeAgUse();
               buildLabels();

            } else if (updateState === "active") { //Update in progress. -------------------------------------
               computeAgUse();
               buildLabels();

            } else if (updateState === "complete") { // ------------------------------------------
            }

         //   setHelpMessage();
         //   refresh();
         });

         //Undo graphic edit event.
         sketchVM.on("undo", function (event) {
         });

         //Redo graphic edit event.
         sketchVM.on("redo", function (event) {
         });

         //Delete graphic event.
         //sketchVM.on("delete", function (event) {
         //});

         //Exit edit graphic mode for map click outside the graphic.
         click_eventHandler = MainMap.activeView.on("click", function (event) {
            MainMap.activeView.hitTest(event).then(function (response: __esri.HitTestResult | __esri.SceneViewHitTestResult) {
               let polygonFound: boolean = false;

               if (response.results.length > 0) {
                  for (const result of response.results) {
                     const layer = result.layer;
                     if (layer && layer.id) {
                        if (layer.id.StartsWith("agUse")) {
                           polygonFound = true;
                        }
                     }
                  }
               }

               if (updateState) {
                  if (!polygonFound && (updateState === "start" || updateState === "active") && agUseType) {
                     sketchVM.cancel();
                  }
               }

            }); //end hit test.
         }); //end map click.

         //Fire up the drawing tool.
         //if (activeTool) {
         activate_sketchVM();
         //}

         setHelpMessage();
         refresh();

         //Component will unmount.
         return () => {
            sketchVM.cancel();
            sketchVM.destroy();
            GraLay.agUse.removeAll();
            GraLay.agUseLabels.removeAll();
            click_eventHandler.remove();
         };

      }).catch((error: Error) => {
         console.error('An error occurred while loading the Sketch View Model module:', error);
      });

   }, []); //end component did mount.

   //Component did update.
   React.useEffect(() => {
      if (printing) {
         printPreview(); //Print page has been rendered.
      }
   });

   //Get parcel polygon success.
   function getParcelGeometry_success(fs: __esri.FeatureSet) {
      if (fs.features.length > 0) {
         activeGra.geometry = fs.features[0].geometry;
         computeAgUse();
         buildLabels(true);
         disableDrawingTool();
      }
   }

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Build labels.
   function buildLabels(complete: boolean = false) {

      //Get existing labels for edit mode.
      if (updateTool === "reshape" || updateTool === "transform") {
         for (const gra_normal of GraLay.agUseLabels.graphics) {
            const gra = gra_normal as Graphic_extended;

            if (gra.attributes.myId) {
               const id: string = gra.attributes.myId;

               if (id.StartsWith(gra.attributes.myId) && id.Contains("_")) {
                  if (id.EndsWith("_Area")) {
                     areaLabelGra = gra;
                  }
               }
            }
         }
      }

      //Label the area.
      let poly = activeGra.geometry as __esri.Polygon;

      if (poly.rings[0].length > 3) {

         //Create empty point graphic for the text symbol.
         if (!areaLabelGra) {
            const textSymbol = JSON.parse(JSON.stringify(totalSymbol)) as __esri.TextSymbol; //Clone it.
            textSymbol.verticalAlignment = "bottom";
            textSymbol.yoffset = 15;

            areaLabelGra = new Graphic({ geometry: new Point({ x: 1, y: 1 }), symbol: textSymbol });
            areaLabelGra.attributes = { myId: activeGra.attributes.myId + "_Area", agUseType: activeGra.attributes.agUseType };
            GraLay.agUseLabels.add(areaLabelGra);
         }

         if (areaText) {
            const cenPnt = Util_App.getPointInsidePolygon(activeGra.geometry as __esri.Polygon);
            areaLabelGra.geometry = cenPnt;

            //Compute area of active graphic in screen units.
            const pntLL = new Point({ x: activeGra.geometry.extent.xmin, y: activeGra.geometry.extent.ymin, spatialReference: MainMap.activeView.spatialReference });
            const pntUR = new Point({ x: activeGra.geometry.extent.xmax, y: activeGra.geometry.extent.ymax, spatialReference: MainMap.activeView.spatialReference });
            const screenLL = MainMap.activeView.toScreen(pntLL);
            const screenUR = MainMap.activeView.toScreen(pntUR);
            const screenArea: number = Math.abs(screenUR.x - screenLL.x) * Math.abs(screenUR.y - screenLL.y);

            const textSym = areaLabelGra.symbol as __esri.TextSymbol;
            textSym.text = "";

            if (screenArea > minScreenAreaForLabel) {
               textSym.text = areaText + " " + areaUnitLabel;
            }
         }
      }

   } //End function.

   //Drawing tool changed.
   function toolbar_changed(tool: string) {
      let i: number;
      let j: number = 0;

      if (tool === "Clear") {
         clearSelected();

      } else if (tool === "Done") {
         const poly = activeGra.geometry as __esri.Polygon;
         const ring = poly.rings[0];

         i = -1;
         for (const pnt of ring) {
            i++;
            if (pnt[0] === curPnt.x && pnt[1] === curPnt.y) {
               j = i;
            }
         }

         poly.removePoint(0, j);
         sketchVM.complete();
         return;

      } else { //Drawing tool was clicked.
         if (drawingState === "active") {
            clearSelected();
         }

         if (tool === agUseType) {
            agUseType = "";
            disableDrawingTool();
            refresh();
            return;
         }

         if (entireParcel) {
            clearAll();
         }

         agUseType = tool;
         activate_sketchVM();
      }

      drawingState = null;
      areaText = "";

      setHelpMessage();
      refresh();
   }

   //Disable drawing tool.
   function disableDrawingTool() {
      sketchVM.cancel();
      agUseType = "";
      //activeTool = null;
      activeGra = null;
      //setHelpMessage();
   }

   //Set the help message.
   function setHelpMessage() {

      if (agUseType === "Timber" || agUseType === "Crop") {
         if (entireParcel) {
            helpMsg = 'Click a parcel on the map to define ag use area';

         } else {
            helpMsg = 'Draw a shape on the map to define ag use area';

            if (drawingState === "start" || drawingState === "active") {
               helpMsg = 'Click to continue or "Done"(double-click) to finish';
            }
         }

         //Update selected polygon graphic.
      } else if (updateState === "start" || updateState === "active") {
         helpMsg = 'Drag an anchor point to edit the polygon';

      } else { //No active drawing tool.
         helpMsg = 'Choose an agricultural type';

         if (GraLay.agUse.graphics.length > 0) {
            helpMsg = 'Choose a tool or select a polygon to edit';
         }
      }
   }

   //Select entire parcel checkbox changed.
   function selectEntireParcelChanged(event: any) {
      entireParcel = event.target.checked;
      //GraLay.agUse.graphics.removeAll();
      activate_sketchVM();
      setHelpMessage();
      refresh();
   }

   //Activate the sketch tool.
   function activate_sketchVM() {
      if (agUseType) {
         if (entireParcel) {
            if (!activeTool || !drawingState || drawingState === "complete" ) {
               sketchVM.create("point");
            }

         } else {
            if (!activeTool || !drawingState || drawingState === "complete") {
               sketchVM.create("polygon");
            }
         }
      }
   }

   //Tax rate changed.
   function taxRateChanged(event: any) {
      taxRate6Percent = event.target.checked;
      refresh();
   }

   //Compute results.
   function computeAgUse() {
      const geom: any = activeGra.geometry;

      const areaUnitAPI = "acres";
      areaUnitLabel = activeGra.attributes.agUseType;

      //Compute area in acres.
      if (MainMap.activeView.spatialReference.isWebMercator) {
         parcelAcres = geometryEngine.geodesicArea(geom, areaUnitAPI);
      } else {
         parcelAcres = geometryEngine.planarArea(geom, areaUnitAPI);
      }

      parcelAcres = Util.round(parcelAcres, 2);
      areaText = Util.formatTextValue(parcelAcres, "Comma", true);

      //Make sure there is a graphic for selection.
      if (GraLay.agUse.graphics.length > 0) {

         //Get centroid of first ag use polygon.
         let gra = GraLay.agUse.graphics.getItemAt(0);
         const cenPnt = Util_App.getPointInsidePolygon(gra.geometry as __esri.Polygon);

         //Get parcel polygon.
         query = new Query({
            returnGeometry: true,
            outSpatialReference: MainMap.activeView.spatialReference,
            geometry: cenPnt,
            maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
            geometryPrecision: 0,
            outFields: [Settings.pin_fieldName, Settings.deedAcresFieldName],
         });

         const parcelLaySpec: Settings.iLayer = Util_App.getLayerSpec(Settings.parcelLayerName);
         const url: string = Util_App.getLayerURL(parcelLaySpec);
         QueryTask.executeQueryJSON(url, query).then(getParcelPolygon_success).catch(queryTask_failed);
      }
   }

   //Get parcel polygon success.
   function getParcelPolygon_success(fs: any) {
      Util.logMsg("Top of AgriculuralUse.getParcelPolygon_success");

      if (fs.features.length > 0) {
         parcelFeature = fs.features[0];
         parcelPoly = parcelFeature.geometry as __esri.Polygon;
         pin = Util_App.getFieldValueFAT(Settings.pin_fieldName, fs.features[0]);
         deedAcres = Util_App.getFieldValueFAT(Settings.deedAcresFieldName, fs.features[0]);

         //Util.errMsg("Deeded & Calced acres: " + deedAcres + " " + parcelAcres);

         //Compute deeded vs actual acres values.
         deedAcresDeviation = 999;

         if (deedAcres > 0 && parcelAcres > 0) {
            deedAcresScaleFactor = deedAcres / parcelAcres;
            deedAcresDeviation = deedAcresScaleFactor;

            if (deedAcresScaleFactor < 1) {
               deedAcresDeviation = parcelAcres / deedAcres;
            }
         }

         //Get the total land value so price per acre can be calculated.
         const ajaxData: Default.iAjaxData = { action: "getTotalLandValueIBM", pinList: [pin] };

         const webAPI_path = Util_App.getWebAPI_path();

         Axios.post(webAPI_path, ajaxData)
            .then(function (response) {
               if (!response) {
                  Util.errMsg("ajax results from server are null");
                  return;
               }
               if (response.data.errMsg !== "") {
                  SnackBar.show(response.data.errMsg);
                  return;
               }
               totalLandValue = response.data.totalLandOrig;

               getIntersectingSoilsPolygons();
            })
            .catch(function (error) {
               Util.errMsg("ajax error getting total land value.", error);
            });
      }
   }

   //Get intersecting soils polygons.
   function getIntersectingSoilsPolygons() {
      Util.logMsg("Top of AgriculuralUse.getIntersectingSoilsPolygons");

      //Build query to get intersecting soils polygons.
      query = new Query({
         returnGeometry: true,
         outSpatialReference: MainMap.activeView.spatialReference,
         geometry: parcelPoly,
         maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
         geometryPrecision: 0,
         outFields: ["*"],
      });

      const soilsLaySpec: Settings.iLayer = Util_App.getLayerSpec(Settings.soilsLayerName);
      const url: string = Util_App.getLayerURL(soilsLaySpec);
      QueryTask.executeQueryJSON(url, query).then(getOverlayFeatures_success).catch(queryTask_failed);
   }

   //Get overlay features success.
   function getOverlayFeatures_success(fs: any) {
      Util.logMsg("Top of getOverlayFeatures_success");

      if (fs.features.length > 0) {
         const overlayFeatures: Util_App.iOverlayfeatures[] = Util_App.getClippedOverlayFeatures(fs.features, parcelPoly, "acres");

         //Build array of features from the clipped soils polygons.
         const clippedSoilsFeatures = [];
         for (const rec of overlayFeatures) {
            clippedSoilsFeatures.Add(rec.feature);
         }

         //Clip the soils by ag use drawings.
         allResults = [];

         for (const gra_normal of GraLay.agUse.graphics) {
            const gra = gra_normal as Graphic_extended;
            const soilOverlayFeatures: Util_App.iOverlayfeatures[] = Util_App.getClippedOverlayFeatures(clippedSoilsFeatures, gra.geometry as __esri.Polygon, "acres");

            //Build all results array.
            for (const rec of soilOverlayFeatures) {

               if (entireParcel) {
                  if (deedAcresDeviation < 1.5) { // 1.5 = 50% in other words.
                     rec.areaOrDistance = rec.areaOrDistance * deedAcresScaleFactor; //Scale actual area to match deeded area.
                  }
               }

               if (rec.areaOrDistance >= minArea) {
                  const soilType: string = Util_App.getFieldValueFAT(Settings.soilTypeFieldName, rec.feature);
                  const taxRate: SoilTaxRate.iTaxRateValue = SoilTaxRate.getTaxRate(gra.attributes.agUseType, soilType);
                  const area2: number = Util.parseFlt(rec.areaOrDistance.toFixed(2)); //Round to nearest hundreth.

                  if (taxRate) {
                     const result: iResult = {
                        agUseType: gra.attributes.agUseType, area: area2, soilType: soilType,
                        taxCode: taxRate.code, taxValue: taxRate.value, gra: rec.feature.clone() as Graphic_extended,
                     };
                     allResults.Add(result);

                  } else {
                     Util.errMsg("Soil type not found in tax rate array: " + soilType);
                  }
               }
            }
         }

         //Process all results.
         totalValue = 0;
         totalAcres = 0;
         results = [];

         if (allResults.length > 0) {

            //Sort the results.
            allResults.sort(Util.sortArray("taxCode"));

            //Highlight clipped soils and show soil types on left panel.
            GraLay.appPoly.removeAll();

            for (const rec of allResults) {
               rec.gra.symbol = Settings.agUseHighlightPolygonSym;
               GraLay.appPoly.add(rec.gra);
            }

            //Combine like soil codes to save to IBM.
            let i: number = 0;
            let prevResult: iResult = JSON.parse(JSON.stringify(allResults[0])); //Clone it.

            for (const rec of allResults) {

               if (rec.taxCode === prevResult.taxCode) {
                  if (i > 0) {
                     prevResult.area += rec.area;
                  }

               } else {
                  results.Add(prevResult);
                  prevResult = JSON.parse(JSON.stringify(rec)); //Clone it.
               }

               //Compute total value.
               totalValue += rec.area * rec.taxValue;
               totalAcres += rec.area;
               i++;
            }

            //Flush out last record
            if (i === allResults.length) {
               results.Add(prevResult);
            }

            totalValue = Math.round(totalValue);
         }
      }
      refresh();
   }

   //Query task failed.
   function queryTask_failed(error: Error): void {
      let msg = "QueryTask failed - ";
      Spinner.hide();

      if (query.where) {
         msg += "Where: " + query.where + " ";
      }
      if (query.outFields) {
         msg += "Output fields: " + query.outFields.toString() + " ";
      }
      Util.errMsg(msg, error);
   }

   //Delete individual selected polygon graphics.
   function clearSelected() {
      GraLay.appPoly.removeAll();

      //Remove labels.
      const gras: any[] = [];
      for (const gra_normal of GraLay.agUseLabels.graphics) {
         const gra = gra_normal as Graphic_extended;

         if (gra.attributes.myId.StartsWith(activeGra.attributes.myId)) {
            gras.Add(gra);
         }
      }
      GraLay.agUseLabels.removeMany(gras);

      //Delete selected graphic if any.
      if (updateState === "start" || updateState === "active") {
         sketchVM.delete();

      } else if (activeTool) {
         sketchVM.cancel();
      }

      setHelpMessage();

      computeAgUse();
      //refresh();
   }

   //Clear all ag use graphics.
   function clearAll() {
      if (activeTool) {
         sketchVM.cancel();
      }

      allResults = [];
      results = [];

      //Remove all graphics.
      GraLay.agUse.removeAll();
      GraLay.agUseLabels.removeAll();
      GraLay.appPoly.removeAll();
      setHelpMessage();

      //Restart active tool if any.
      //activate_sketchVM();
      refresh();
   }

   //Save values.
   function saveToIBM() {
      if (Spinner.busy) {
         return; //Quit if busy.
      }
      Spinner.show();

      //Build list of soil types and quantities.
      results.sort(Util.sortArray_reverse("area")); //Sort so largest areas are first.
      let codeQty: string = "";

      for (const rec of results) {
         if (codeQty !== "") {
            codeQty += "|";
         }
         codeQty += rec.taxCode + "," + rec.area;
      }

      const ajaxData: Default.iAjaxData = {
         action: "saveAgUseIBM", userName: SignIn_Auth0.userName, pinList: [pin],
         percentOrValue: totalValue, skipDoneIBM: taxRate6Percent, adjustType: codeQty,
      };

      const webAPI_path = Util_App.getWebAPI_path();

      Axios.post(webAPI_path, ajaxData)
         .then(function (response) {
            setTimeout(function () { Spinner.hide(); }, 500); //Wait to avoid fumble finger multiple clicks.

            if (!response) {
               Util.errMsg("ajax results from server are null");
               return;
            }
            if (response.data.errMsg !== "") {
               SnackBar.show(response.data.errMsg);
               return;
            }
            SnackBar.show("Successfully saved agricultural use", 2);
         })
         .catch(function (error) {
            Spinner.hide();
            Util.errMsg("ajax error setting IBM values.", error);
         });
   }

   //Get buttons.
   function getButtons(): any[] {
      const toolNames: string[] = ["Timber", "Crop"];

      //Add button to finish off a drawing.
      let poly: __esri.Polygon;
      if (activeGra) {
         poly = activeGra.geometry as __esri.Polygon;
      }

      if (drawingState === "active" && poly && poly.rings[0].length > 4) {
         toolNames.Add("Done");

      } else {
         toolNames.Add("Clear");
      }

      const jsx = [];

      for (const tool of toolNames) {
         if (!tool.StartsWith(" ")) {
            const ti: Settings.iToolInfo = Settings.getToolInfo(tool);

            let backColor: ButtonProps["color"] = "primary";
            if (agUseType === tool) {
               backColor = "secondary";
            }

            let disabled: boolean = false;
            if (tool === "Clear" && (updateState !== "start" && updateState !== "active")) {
               disabled = true;
            }
            if (tool === "Clear" && drawingState === "active") {
               disabled = false;
            }

            const icon: any = <Icon color='action'>{ti.iconName}</Icon>;

            jsx.push(
               <Button key={Util.uid()} startIcon={icon} disabled={disabled} color={backColor} onClick={toolbar_changed.bind(null, tool)} title={ti.tip} aria-label={tool}>
                  {ti.label}
               </Button>,
            );
         }
      }
      return jsx;
   }

   //Get all result text.
   function getAllResultText(): any[] {
      const jsx = [];

      for (const rec of allResults) {
         const acres: number = Util.round(rec.area, 2);
         jsx.push(<Typography key={Util.uid()} variant="body2">{acres} acres of {rec.soilType}</Typography>);
      }
      return jsx;
   }

   //Get combined result text.
   function getResultText(): any[] {
      const jsx = [];

      for (const rec of results) {
         const acres: number = Util.round(rec.area, 2);

         let taxCodeText: string = rec.taxCode;
         if (rec.taxCode.Contains("T")) {
            taxCodeText = taxCodeText.replace("T", "");
            taxCodeText += " Timber";
         } else {
            taxCodeText = taxCodeText.replace("C", "");
            taxCodeText += " Crop";
         }

         jsx.push(<Typography key={Util.uid()} variant="body2">{acres} acres of {taxCodeText} at ${rec.taxValue}</Typography>);
      }
      return jsx;
   }

   //1) Render to print div.
   function handlePrint() {
      printing = true;

      if (!Settings.printDivRoot) {
         Settings.setPrintDivRoot(ReactDOM.createRoot(document.getElementById("print_div")));
      }
      Settings.printDivRoot.render(<Render />);
   }

   //2) Show print preview.
   function printPreview() {
      printing = false;
      window.print();
      Widgets.show("Agricultural Use"); //Force re-render in Widgets.tsx
   }

   let printIconVisible: boolean = false;
   if (!printing && results.length > 0) {
      printIconVisible = true;
   }

   let closeIconVisible: boolean = false;
   if (!printing) {
      closeIconVisible = true;
   }

   //Set results visibility.
   //Util.errMsg("ActTool DraSta UpSta: " + activeTool + ", " + drawingState + ", " + updateState);
   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   let resultsVisible: boolean = false;
   if (results.length > 0) {
      resultsVisible = true;
   }

   let clearAllDisabled: boolean = false;
   if (GraLay.agUse.graphics.length === 0) {
      clearAllDisabled = true;
   }

   //Save button.
   let saveDisabled: boolean = true;
   if (results.length > 0 && Settings.security.accessLevel >= 10) {
      saveDisabled = false;
   }

   //Compute total tax.
   //let totalTax: number;

   //if (taxRate6Percent) {
   //   totalTax = totalValue * 0.06;
   //} else {
   //   totalTax = totalValue * 0.04;
   //}
   //totalTax = Util.round(totalTax, 2);
   //const totalTaxText: string = Util.formatTextValue(totalTax, "Comma", true);

   //Compute ag use values.
   const agUseValueText: string = Util.formatTextValue(totalValue, "Comma", true);

   //Util.errMsg("totalAcres: " + totalAcres.toString());

   const totalAcresText: string = Util.formatTextValue(totalAcres, "Comma", true);
   const pricePerAcreText: string = Util.formatTextValue(Math.round(totalLandValue / totalAcres), "Money");

   //Styles.
   const styles: any = {
      toolStyle: { width: Settings.leftPanelWidth, maxWidth: Settings.leftPanelWidth, padding: 5, paddingTop: 0, paddingBottom: 3, overflow: 'auto' },
      help: { marginTop: 15, padding: 5, backgroundColor: yellow[200] },
      col1Container: {
         display: 'flex', flexDirection: 'column', justifyContent: 'space-around', alignItems: 'flex-start',
         backgroundColor: blueGrey[50], marginTop: 4, padding: 7, paddingTop: 4, borderRadius: 4,
      },
   };

   //ThemeProvider is required for printing.
   return (
      <ThemeProvider theme={Default.theme}>
         <CssBaseline />
         <Paper>
            <div style={Default.styles.appToolHeading}>
               <Typography variant="body1" style={{ marginLeft: 12 }}>
                  {toolName}
               </Typography>

               <ExpandOnPhone.Render />

               {printIconVisible &&
                  <IconButton color="default" onClick={handlePrint} title="Print" aria-label="Print" >
                     <Icon>print</Icon>
                  </IconButton>
               }

               {closeIconVisible &&
                  <IconButton color="default" onClick={close} title="Close" aria-label="Close">
                     <Icon>close</Icon>
                  </IconButton>
               }
            </div>

            <div id={scrollDivID} style={Default.styles.appToolDiv}>
               {!printing && <div>
                  <ButtonGroup variant="contained" fullWidth aria-label="Agricultural Use toolbar buttons">
                     {getButtons()}
                  </ButtonGroup>

                  <Typography variant="body2" style={styles.help}>
                     {helpMsg}
                  </Typography>

                  <div className="app-flex-row-start" style={{ marginLeft: 9, marginRight: 9 }}>
                     <div>
                        <FormControlLabel title="Select entire parcel"
                           control={<Checkbox checked={entireParcel} color="primary" onChange={selectEntireParcelChanged} />}
                           label="Select entire parcel"
                        />
                        <FormControlLabel title="Set 6% tax rate"
                           control={<Checkbox checked={taxRate6Percent} color="primary" onChange={taxRateChanged} />}
                           label="6% tax (10 or more owners)"
                        />
                     </div>

                     <Button variant="outlined" size="medium" color="primary" onClick={clearAll} disabled={clearAllDisabled} title="Clear all" aria-label="Clear all">
                        Clear All
                     </Button>
                  </div>
               </div>}

               {resultsVisible && <div>
                  <div style={styles.col1Container}>
                     {getAllResultText()}
                  </div>

                  <div style={styles.col1Container}>
                     {getResultText()}
                  </div>

                  <Typography variant="body1" style={{ marginTop: 5, marginLeft: 3 }}>{totalAcresText} Acres &nbsp;&nbsp; Ag Use: {agUseValueText}</Typography>
                  <Typography variant="body1" style={{ marginTop: 5, marginLeft: 3 }}>PIN: {pin} &nbsp;&nbsp; Price/acre: {pricePerAcreText}</Typography>
               </div>}

               {!printing && <div className="app-flex-row-center" style={{ marginTop: 5 }}>
                  <Button variant="contained" size="medium" color="primary" onClick={saveToIBM} disabled={saveDisabled} title="Save to IBM">
                     Save
                  </Button>
               </div>}
            </div>

         </Paper>
      </ThemeProvider>
   ); //end return.

} //end function.
